import { useUser } from '@clerk/clerk-react'
import { Building2, Loader2, Pencil, X } from 'lucide-react'
import { useOptimistic, useState, useTransition } from 'react'
import { toast } from 'sonner'

import { type Clinic, type ClinicId, getClinic } from '@snootsvet/shared'

import { ClinicSearch } from '@/frontend/components/clinic-search'
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/frontend/components/ui/combobox'
import { useRegionClinics } from '@/frontend/features/region/use-region-clinics'
import { useUserMetadata } from '@/frontend/hooks/use-user-metadata'

interface DefaultClinicSelectorProps {
  id?: string
  className?: string
  /** Whether the selection can be cleared back to "no default". Defaults to `true`. */
  allowClear?: boolean
  /**
   * `inline` (default) renders the combobox directly — suited to settings forms.
   * `chip` renders a compact read-only chip that opens the search in a dropdown
   * on click — suited to the top bar, where a persistent combobox is noisy.
   */
  variant?: 'inline' | 'chip'
}

/**
 * Self-contained picker for the user's default clinic preference. Owns reading
 * the current value, the optimistic write to Clerk metadata, and the pending /
 * toast feedback — consumers just drop it in (settings modal, top bar, etc).
 */
export function DefaultClinicSelector({
  id,
  className,
  allowClear = true,
  variant = 'inline',
}: DefaultClinicSelectorProps) {
  const { settings } = useUserMetadata()
  const { user } = useUser()

  const [optimisticId, setOptimisticId] = useOptimistic(settings?.defaultClinicId ?? null)
  const [isPending, startTransition] = useTransition()
  // Controlled so the "Clear default clinic" footer action can close the dropdown.
  const [open, setOpen] = useState(false)
  // Only offer clinics in the active region. A default clinic in another region
  // is intentionally not shown here while that region isn't active.
  const regionClinics = useRegionClinics()

  function handleChange(clinicId: ClinicId | null) {
    if (!user) return
    startTransition(async () => {
      setOptimisticId(clinicId)
      try {
        await user.update({
          unsafeMetadata: { ...user.unsafeMetadata, defaultClinicId: clinicId },
        })
        const name = clinicId ? getClinic(clinicId)?.name : null
        toast.success(name ? `Default clinic set to ${name}` : 'Default clinic cleared')
      } catch {
        toast.error('Failed to update default clinic')
      }
    })
  }

  if (variant === 'chip') {
    // Show the default only when it belongs to the active region, so an
    // out-of-region default reads as "No default clinic" here rather than
    // surfacing a clinic from another region.
    const selected = optimisticId
      ? (regionClinics.find((c) => c.id === optimisticId) ?? null)
      : null
    return (
      <Combobox<Clinic>
        open={open}
        onOpenChange={setOpen}
        value={selected}
        onValueChange={(clinic) => {
          if (!clinic && !allowClear) return
          handleChange(clinic ? clinic.id : null)
        }}
        isItemEqualToValue={(a, b) => a.id === b.id}
        itemToStringLabel={(clinic) => clinic.name}
        items={regionClinics}
      >
        <div className="inline-flex h-8 items-center gap-1.5 px-1 text-sm text-muted-foreground">
          <Building2 className="size-3.5 shrink-0" />
          <span className="max-w-32 truncate">{selected?.name ?? 'No default clinic'}</span>
          <ComboboxTrigger
            render={
              <button
                type="button"
                aria-label="Change default clinic"
                className="inline-flex size-5 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              />
            }
          >
            {isPending ? (
              <Loader2 className="size-3 shrink-0 animate-spin" />
            ) : (
              <Pencil className="size-3 shrink-0" />
            )}
          </ComboboxTrigger>
        </div>
        <ComboboxContent className="w-[28rem]" align="end" backdrop>
          <ComboboxInput placeholder="Search clinics..." showTrigger={false} className="border-0" />
          <ComboboxList className="max-h-[28rem]">
            <ComboboxEmpty>No clinics found.</ComboboxEmpty>
            <ComboboxCollection>
              {(clinic) => (
                <ComboboxItem key={clinic.id} value={clinic}>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{clinic.name}</span>
                    <span className="text-xs whitespace-nowrap text-muted-foreground">
                      {clinic.address}
                    </span>
                  </div>
                </ComboboxItem>
              )}
            </ComboboxCollection>
          </ComboboxList>
          {/* Persistent clear action — stays visible below the (scrollable,
              search-filtered) list so the user can always reset to no default. */}
          {allowClear && selected && (
            <div className="border-t p-1">
              <button
                type="button"
                onClick={() => {
                  handleChange(null)
                  setOpen(false)
                }}
                className="flex w-full cursor-pointer items-center gap-2 rounded-md px-1.5 py-1.5 text-sm font-semibold text-destructive-fg outline-hidden transition-colors hover:bg-destructive"
              >
                <X className="size-4 shrink-0" />
                Clear default clinic
              </button>
            </div>
          )}
        </ComboboxContent>
      </Combobox>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <ClinicSearch
        id={id}
        // Show the default only when it's in the active region — an out-of-region
        // default reads as "no default" here (matching the chip) rather than being
        // wiped by ClinicSearch's out-of-region clearing.
        value={regionClinics.some((c) => c.id === optimisticId) ? optimisticId : null}
        onChange={handleChange}
        allowClear={allowClear}
        placeholder="No default clinic"
        className={className}
      />
      {isPending && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
    </div>
  )
}
